import { Editor } from "@vericus/cadmus-editor-prosemirror";

import { captureException } from "@sentry/react";
import JSZip, { JSZipObject } from "jszip";
import filetype from "magic-bytes.js";
import { v4 as uuid } from "uuid";

import {
  BaseType,
  Cardinality,
  Choice,
  PositionAreaInput,
  PromptImage,
  QuestionFieldInput,
  QuestionType,
  Shape,
  SimpleChoice,
} from "@/generated/graphql";

import { ImportableQuestionType, ParsedQuestion } from "../../data/types";
import {
  Base64File,
  patchHTML as basePatchHTML,
  createQuestionPreviewEditor,
  decodeHtmlEntities,
  getBase64Mime,
} from "../utils";
import {
  ConditionVar,
  QTIMaterial,
  QTIQuestion,
  QTIResource,
  QTIResponseCondition,
  QTIResponseConditionVar,
  QTIResponseContent,
} from "./types";
import { getResponseId } from "./utils";

/**
 * Parse question type based on known qti metadata from things like
 * blackboard and canvas.
 *
 * Note: The QTI 2x parser doesn't read the Metadata and so this function
 * won't be able to determine the question type based on the metadata.
 * It can however determine the question type based on the responses, so
 * there's no problem beyond having to do extra work.
 **/
function getMetadataQuestionType(question: QTIQuestion) {
  const maybeQuestionType = (question.metadata.question_type ??
    question.metadata.questiontype) as unknown as string | undefined;
  switch (maybeQuestionType) {
    // canvas' question type meta
    case "multiple_choice_question": {
      return QuestionType.Mcq;
    }
    case "true_false_question": {
      return QuestionType.Truefalse;
    }
    case "essay_question": {
      return QuestionType.Extended;
    }
    case "fill_in_multiple_blanks_question": {
      return QuestionType.Blanks;
    }
    case "matching_question": {
      return QuestionType.Matching;
    }
    case "numerical_question": {
      return QuestionType.Numerical;
    }
    case "hot_spot_question": {
      return QuestionType.Hotspot;
    }
    // blackboard question type meta
    case "Fill in the Blank Plus": {
      return QuestionType.Blanks;
    }
    case "Matching": {
      return QuestionType.Matching;
    }
    case "Numeric": {
      return QuestionType.Numerical;
    }
    case "Short Response": {
      return QuestionType.Short;
    }
    case "Multiple Answer": {
      return QuestionType.Mcq;
    }
    case "Multiple Choice": {
      return QuestionType.Mcq;
    }
    case "Either/Or": {
      return QuestionType.Truefalse;
    }
    case "Essay": {
      return QuestionType.Extended;
    }
    default:
      return null;
  }
}

/**
 * Check from Question Type metadata or the question itself
 * to figure out if it's a true or false question.
 * @param question QTI question parsed to JSON format
 * @param maybeQuestionType QTI's question type parsed from metadata
 * @returns is question a True and False question
 **/
function isTFQuestion(
  question: QTIQuestion,
  maybeQuestionType: QuestionType | null
) {
  // If question type exists in the metadata then return true or false
  // based on whether it says it's a true and false question or not
  if (maybeQuestionType && maybeQuestionType !== QuestionType.Truefalse)
    return false;
  if (maybeQuestionType === QuestionType.Truefalse) return true;
  const { responses } = question;
  // Check if it has a single response of type lid/identifier and
  // has 2 choices which consist of true and false
  if (
    responses.length === 1 &&
    responses[0] &&
    responses[0].cardinality === Cardinality.Single &&
    Array.isArray(responses[0].choiceResponse?.choices) &&
    responses[0].choiceResponse.choices?.length === 2 &&
    responses[0].type &&
    ["lid", "identifier"].includes(responses[0].type) &&
    responses[0].choiceResponse.choices.every((choice) =>
      choice.contents.some((c) => /true|false/gi.test(c.text ?? ""))
    )
  ) {
    // Make sure that it is block type interaction as otherwise prompts will have blanks (>qti2.x)
    if (
      responses?.[0]?.interactionType &&
      responses?.[0].interactionType !== "choiceInteraction"
    ) {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * Check from Question Type metadata or the question itself
 * to figure out if it's an MCQ question.
 * @param question QTI question parsed to JSON format
 * @param maybeQuestionType QTI's question type parsed from metadata
 * @returns is question an MCQ question
 **/
function isMCQQuestion(
  question: QTIQuestion,
  maybeQuestionType: QuestionType | null
) {
  // Check question type from the metadata if exist. Returns true/false based on
  // if question type metadata says it's an mcq question
  if (maybeQuestionType && maybeQuestionType !== QuestionType.Mcq) return false;
  if (maybeQuestionType === QuestionType.Mcq) return true;
  const { responses, respConditions } = question;

  // Check if it has a single response of type lid/identifier and
  // it has correct response(s) marked
  if (
    responses.length === 1 &&
    responses[0] &&
    [Cardinality.Single, Cardinality.Multiple].includes(
      responses[0].cardinality
    ) &&
    responses[0].type &&
    ["lid", "identifier"].includes(responses[0].type) &&
    responses[0].choiceResponse?.choices.some((c) =>
      getMCQIsCorrect(c.id, respConditions, responses[0]?.correctResponses)
    )
  ) {
    // Make sure that it is block type interaction as otherwise prompts will have blanks
    if (
      responses?.[0].interactionType &&
      responses?.[0].interactionType !== "choiceInteraction"
    ) {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * Check from Question Type metadata or the question itself
 * to figure out if it's an extended answer question.
 * @param question QTI question parsed to JSON format
 * @param maybeQuestionType QTI's question type parsed from metadata
 * @returns is question an extended answer question
 **/
function isExtendedQuestion(
  question: QTIQuestion,
  maybeQuestionType: QuestionType | null
) {
  // Check question type from the metadata if exist. Returns true/false based on
  // if question type metadata says it's an extended answer question
  if (maybeQuestionType && maybeQuestionType !== QuestionType.Extended)
    return false;
  if (maybeQuestionType === QuestionType.Extended) return true;
  const { responses, respConditions } = question;
  // Check if it has a single response of type str/string and
  // it's an extendedTextInteraction interaction type (if qti2.x) and
  // it has no correct responses marked or it has wordLimit
  if (
    responses.length === 1 &&
    responses[0] &&
    responses[0].cardinality === Cardinality.Single &&
    responses[0].type &&
    ["str", "string"].includes(responses[0].type)
  ) {
    if (
      responses?.[0].interactionType &&
      responses?.[0].interactionType !== "extendedTextInteraction"
    ) {
      return false;
    }
    return (
      !respConditions?.some(
        (r) =>
          r.conditions?.findIndex(({ condition }) => condition?.value) !== -1
      ) || responses[0].wordLimit
    );
  }
  return false;
}

/**
 * Check from Question Type metadata or the question itself
 * to figure out if it's a short answer question.
 * @param question QTI question parsed to JSON format
 * @param maybeQuestionType QTI's question type parsed from metadata
 * @returns is question a short answer question
 **/
function isShortQuestion(
  question: QTIQuestion,
  maybeQuestionType: QuestionType | null
) {
  // Check question type from the metadata if exist. Returns true/false based on
  // if question type metadata says it's a short answer question
  if (maybeQuestionType && maybeQuestionType !== QuestionType.Short)
    return false;
  if (maybeQuestionType === QuestionType.Short) return true;
  const { responses, respConditions } = question;
  // Check if it has a single response of type str/string and
  // it's not a textEntryInteraction interaction type (if qti2.x) and
  // it has correct reponses marked
  if (
    responses.length === 1 &&
    responses[0] &&
    responses[0].cardinality === Cardinality.Single &&
    responses[0].type &&
    ["str", "string"].includes(responses[0].type)
  ) {
    if (
      responses?.[0].interactionType &&
      responses?.[0].interactionType === "textEntryInteraction"
    ) {
      return false;
    }
    return !!(
      respConditions?.some(
        (r) =>
          r.conditions?.findIndex(({ condition }) => condition?.value) !== -1
      ) || responses[0].correctResponses?.length
    );
  }
  return false;
}

/**
 * Check from Question Type metadata or the question itself
 * to figure out if it's a numerical answer question.
 * @param question QTI question parsed to JSON format
 * @param maybeQuestionType QTI's question type parsed from metadata
 * @returns is question a numerical answer question
 **/
function isNumericalQuestion(
  question: QTIQuestion,
  maybeQuestionType: QuestionType | null
) {
  // Check question type from the metadata if exist. Returns true/false based on
  // if question type metadata says it's a numerical answer question
  if (maybeQuestionType && maybeQuestionType !== QuestionType.Numerical)
    return false;
  if (maybeQuestionType === QuestionType.Numerical) return true;
  const { responses, respConditions } = question;
  // Check if it has a single response of type str/string and
  // it's not a textEntryInteraction interaction type (if qti2.x) and
  // it has correct reponses marked
  if (
    responses.length === 1 &&
    responses[0] &&
    responses[0].cardinality === Cardinality.Single &&
    responses[0].type &&
    ["str", "string"].includes(responses[0].type)
  ) {
    if (
      responses?.[0].interactionType &&
      responses?.[0].interactionType === "textEntryInteraction"
    ) {
      return false;
    }
    return !!(
      respConditions?.some(
        (r) =>
          r.conditions?.findIndex(({ condition }) => condition?.value) !== -1
      ) || responses[0].correctResponses?.length
    );
  }
  return false;
}

/**
 * Check from Question Type metadata or the question itself
 * to figure out if it's an FITB question.
 * @param question QTI question parsed to JSON format
 * @param maybeQuestionType QTI's question type parsed from metadata
 * @returns is question an FITB question
 **/
function isBlankQuestion(
  question: QTIQuestion,
  maybeQuestionType: QuestionType | null
) {
  // Check question type from the metadata if exist. Returns true/false based on
  // if question type metadata says it's an FITB question
  if (maybeQuestionType && maybeQuestionType !== QuestionType.Blanks)
    return false;
  if (maybeQuestionType === QuestionType.Blanks) return true;
  const { responses, contents } = question;
  // Check if it has at least one response of type str/string/lid/identifier and
  // they have correct responses marked.
  if (
    responses.length &&
    responses.every((r) => {
      const id = getResponseId(r.id);
      if (!id) return false;
      if (r.type && !["string", "str", "lid", "identifier"].includes(r.type))
        return false;
      return contents.some((c) => c.text?.search(id) !== -1);
    })
  ) {
    return true;
  }
  return false;
}

/**
 * Check from Question Type metadata or the question itself
 * to figure out if it's a matching question.
 * @param question QTI question parsed to JSON format
 * @param maybeQuestionType QTI's question type parsed from metadata
 * @returns is question a matching question
 **/
function isMatchingQuestion(
  question: QTIQuestion,
  maybeQuestionType: QuestionType | null
) {
  const { responses } = question;
  if (maybeQuestionType && maybeQuestionType !== QuestionType.Matching)
    return false;

  if (maybeQuestionType === QuestionType.Matching) return true;
  return responses.some(
    (r) => r.interactionType && /^match/gi.test(r.interactionType)
  );
}
const unsupportedHotspotInteractions: readonly string[] = [
  "graphicAssociateInteraction",
  "graphicGapMatchInteraction",
];

function isHotspotQuestion(
  question: QTIQuestion,
  maybeQuestionType: QuestionType | null
) {
  const { promptImage, responses } = question;
  if (maybeQuestionType && maybeQuestionType !== QuestionType.Hotspot)
    return false;
  if (promptImage && responses[0]) {
    if (!responses[0]?.interactionType) {
      return true;
    } else {
      if (
        unsupportedHotspotInteractions.includes(responses[0].interactionType)
      ) {
        return false;
      }
      return true;
    }
  }
  return false;
}

/**
 * get Cadmus' question type based on QTI responses and question type metadata
 * even though question type suggest that it is of type, based on the responses
 * it might require to be mapped to a different type or dropped all together
 * since we don't have a support for it yet.
 **/
function getQuestionType(question?: QTIQuestion) {
  if (!question) return null;
  const questionTypeMetadata = getMetadataQuestionType(question);
  if (isTFQuestion(question, questionTypeMetadata)) {
    return QuestionType.Truefalse;
  } else if (isMCQQuestion(question, questionTypeMetadata)) {
    return QuestionType.Mcq;
  } else if (isShortQuestion(question, questionTypeMetadata)) {
    return QuestionType.Short;
  } else if (isExtendedQuestion(question, questionTypeMetadata)) {
    return QuestionType.Extended;
  } else if (isBlankQuestion(question, questionTypeMetadata)) {
    return QuestionType.Blanks;
  } else if (isNumericalQuestion(question, questionTypeMetadata)) {
    return QuestionType.Numerical;
  } else if (isMatchingQuestion(question, questionTypeMetadata)) {
    return QuestionType.Matching;
  } else if (isHotspotQuestion(question, questionTypeMetadata)) {
    return QuestionType.Hotspot;
  }
  return null;
}

/**
 * Map QTI Question to ParsedQuestion
 **/
export async function convertQTIQuestion(
  question?: QTIQuestion,
  file?: JSZipObject | null,
  zip?: JSZip,
  resources?: QTIResource[]
): Promise<Omit<ParsedQuestion, "fileOriginId"> | undefined> {
  if (!question) return undefined;
  const convertFileToBase64 = getBase64File(file, zip, resources);
  const { metadata, feedbacks, outcomes } = question;
  const questionId = (metadata.assessment_question_identifierref ??
    uuid()) as unknown as string;
  const defaultMaxScore = outcomes?.["MAXSCORE"]?.default;
  let maxScore: number | undefined;
  if (typeof defaultMaxScore === "string") {
    const type = outcomes?.["MAXSCORE"]?.type;
    switch (type) {
      case "float":
        maxScore = parseFloat(defaultMaxScore);
        break;
      case "integer":
        maxScore = parseInt(defaultMaxScore, 10);
        break;
    }
  } else {
    maxScore = defaultMaxScore as number | undefined;
  }
  const points =
    maxScore ??
    Object.entries(outcomes).reduce(
      (acc, [_k, outcome]) => {
        const { max } = outcome;
        if (!acc) return max;
        if (!max) return acc;
        return acc + max;
      },
      undefined as number | undefined
    );
  const generalFeedback = feedbacks.find((f) =>
    /^general((?!correct).)*$/.test(f.id ?? "")
  );

  const questionType = getQuestionType(question);

  const patchedContents = patchNonInteractionBlanks(question, questionType);

  const { prompt, shortPrompt } = await getPromptAndShortPrompt(
    patchedContents,
    file,
    zip,
    resources
  );

  const feedback =
    generalFeedback?.contents && generalFeedback.contents.length > 0
      ? await getContentsText(generalFeedback.contents)
      : undefined;

  const promptImage =
    questionType === QuestionType.Hotspot
      ? await getPromptImage(question, convertFileToBase64)
      : undefined;
  let fields: QuestionFieldInput[] = [];
  if (questionType) {
    try {
      fields = await getFields(question, questionType, promptImage);
    } catch (error) {
      captureException("Error parsing question fields", {
        extra: {
          question,
          error,
        },
      });
    }
  }

  const shuffle =
    questionType === QuestionType.Mcq && question.responses?.[0]?.choiceResponse
      ? (question.responses?.[0]?.choiceResponse?.shuffle ?? false)
      : false;

  return {
    questionId,
    points: points ? Math.ceil(points) : 1,
    feedback,
    // if we can't find any fields then it's unknown question type
    questionType: fields.length ? questionType : null,
    promptImage,
    prompt,
    shortPrompt,
    fields,
    shuffle,
  };
}

async function getPromptAndShortPrompt(
  contents: QTIMaterial[],
  file?: JSZipObject | null,
  zip?: JSZip,
  resources?: QTIResource[]
) {
  const promptEditor = await getConcatenatedEditor(
    contents,
    file,
    zip,
    resources
  );
  try {
    const prompt = JSON.stringify(promptEditor?.getJSON() ?? "");
    const shortPrompt = promptEditor?.getText().trim().slice?.(0, 200) ?? "";
    return { prompt, shortPrompt };
  } finally {
    promptEditor?.destroy();
  }
}

async function getPromptImage(
  question: QTIQuestion,
  convertFileToBase64: (url: string) => Promise<Base64File | null>
): Promise<PromptImage | null> {
  const { promptImage } = question;
  if (!promptImage) return null;
  const { attributes } = promptImage;
  if (!attributes.uri) return null;
  const base64 = await convertFileToBase64(attributes.uri);
  if (!base64) return null;
  const { base64String } = base64;
  const mime = getBase64Mime(base64String);
  const imageURL = `data:${mime};base64,${base64String}`;
  const { width, height } = await new Promise<{
    width: number;
    height: number;
  }>((resolve) => {
    if (attributes.width && attributes.height)
      resolve({
        width: parseFloat(attributes.width),
        height: parseFloat(attributes.height),
      });
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.src = imageURL;
  });
  return {
    url: imageURL,
    width,
    height,
    layers: [],
  };
}

/**
 * Map QTI responses and respConditions to fields
 **/
async function getFields(
  question: QTIQuestion,
  questionType: ImportableQuestionType,
  promptImage?: PromptImage | null
) {
  const fields: QuestionFieldInput[] = [];
  if (!questionType) return fields;
  switch (questionType) {
    case QuestionType.Numerical: {
      const { respConditions, responses } = question;

      let correctValues: string[] = [];

      if (responses[0]?.correctResponses) {
        correctValues = responses[0].correctResponses;
      } else if (respConditions?.[0]?.conditions) {
        correctValues = respConditions.flatMap(
          (respCond) =>
            respCond.conditions?.flatMap(
              (c) =>
                c.conditions?.flatMap((c) =>
                  c.condition?.value ? [c.condition.value] : []
                ) ?? ([] as string[])
            ) ?? ([] as string[])
        );
      }

      const field: QuestionFieldInput = {
        identifier: "RESPONSE",
        textEntryInteraction: {},
        response: {
          correctValues,
          matchSimilarity: 1,
          caseSensitive: false,
          baseType: BaseType.Float,
        },
      };
      fields.push(field);
      break;
    }

    case QuestionType.Matching: {
      const { responses, respConditions } = question;

      const targetSet = new Map<string, SimpleChoice>();
      const sourceSet = new Map<string, SimpleChoice>();
      let correctValues: string[] = [];

      await Promise.all(
        responses.map(async (r) => {
          const { id, choiceResponse, correctResponses } = r;
          const responseId = getResponseId(id);
          // Parse all targetSet and sourceSet from various different possible
          // format
          correctResponses?.forEach((r) => correctValues.push(r));
          if (responseId && choiceResponse?.contents) {
            targetSet.set(responseId, {
              identifier: responseId,
              content: await getContentsText(choiceResponse?.contents),
            });
          }
          if (choiceResponse?.choices) {
            await Promise.all(
              choiceResponse.choices.map(async (choice) => {
                const id = getResponseId(choice.id);
                if (!id) return;
                if (correctValues.length) {
                  const sourceIds = correctValues.map((c) => c.split(" ")?.[1]);
                  if (sourceIds.includes(choice.id)) {
                    sourceSet.set(id, {
                      identifier: id,
                      content: await getContentsText(choice.contents),
                    });
                  } else {
                    targetSet.set(id, {
                      identifier: id,
                      content: await getContentsText(choice.contents),
                    });
                  }
                } else {
                  sourceSet.set(id, {
                    identifier: id,
                    content: await getContentsText(choice.contents),
                  });
                }
              })
            );
          }
        })
      );

      // Parse correct values from respConditions if not explicitly specify
      if (!correctValues.length) {
        correctValues = respConditions.flatMap((respCond) => {
          const conditions = respCond?.conditions?.[0];

          if (!conditions || !conditions.condition) return [];

          const targetItemId = getResponseId(
            conditions.condition?.value?.trim() ?? ""
          );
          const sourceItemId = getResponseId(conditions.condition.id.trim());

          return [`${sourceItemId} ${targetItemId}`];
        });
      }

      // sourceSet and targetSet might have duplicates with the same content.
      // Cadmus combine duplicates into the same choice identifier hence
      // the need to combine duplicates without affecting correct answers
      const sourceIds = correctValues.map((v) => v.split(" ")?.[1]);
      const targetIds = correctValues.map((v) => v.split(" ")?.[0]);
      const sourceContents = new Map<string, string>();
      const targetContents = new Map<string, string>();
      // cleanup duplicates target & source set
      [...targetSet.values()].forEach((choice) => {
        const { identifier, content } = choice;
        if (!targetIds.includes(identifier)) {
          [...targetSet.values()]
            .filter(
              (s) => s.content === content && !targetIds.includes(s.identifier)
            )
            .forEach(({ identifier, content }) => {
              if (
                targetContents.has(content) &&
                targetContents.get(content) !== identifier
              ) {
                targetSet.delete(identifier);
              }
              if (!targetContents.has(content)) {
                targetContents.set(content, identifier);
              }
            });
        }
      });
      [...sourceSet.values()].forEach((choice) => {
        const { identifier, content } = choice;

        if (!sourceIds.includes(identifier)) {
          [...sourceSet.values()]
            .filter(
              (s) => s.content === content && !sourceIds.includes(s.identifier)
            )
            .forEach(({ identifier, content }) => {
              if (
                sourceContents.has(content) &&
                sourceContents.get(content) !== identifier
              ) {
                sourceSet.delete(identifier);
              }
              if (!sourceContents.has(content)) {
                // Find source with the same content where it is marked as an answer
                const correctIndentifier = [...sourceSet.values()]
                  .filter((s) => s.content === content)
                  .find(({ identifier }) =>
                    sourceIds.includes(identifier)
                  )?.identifier;
                sourceContents.set(content, correctIndentifier ?? identifier);
                // If there is duplicate option where it is marked as an option,
                // remove duplicates which identifier is not makred as answer
                if (correctIndentifier && identifier !== correctIndentifier) {
                  sourceSet.delete(identifier);
                }
              }
            });
        }
      });

      const field: QuestionFieldInput = {
        identifier: "RESPONSE",
        matchInteraction: {
          targetSet: [...targetSet.values()],
          sourceSet: [...sourceSet.values()],
        },
        response: {
          correctValues: correctValues,
        },
      };

      fields.push(field);
      break;
    }

    case QuestionType.Mcq: {
      const { responses, respConditions } = question;
      const response = responses[0];
      if (!response?.choiceResponse) return fields;

      const correctValues: string[] = [];
      const choices: SimpleChoice[] = (
        await getMCQChoices(
          response.choiceResponse.choices,
          respConditions,
          response.correctResponses
        )
      ).map((choice) => {
        if (choice.isCorrect) {
          correctValues.push(choice.id);
        }
        return {
          identifier: choice.id,
          content: choice.text,
        };
      });

      if (!choices) return fields;

      const field: QuestionFieldInput = {
        identifier: "RESPONSE",
        choiceInteraction: {
          choices: choices,
          maxChoices: correctValues.length,
        },
        response: {
          correctValues: correctValues,
        },
      };

      fields.push(field);
      break;
    }
    case QuestionType.Truefalse: {
      const field: QuestionFieldInput = {
        identifier: "RESPONSE",
        choiceInteraction: {
          choices: [
            { identifier: "true", content: "True" },
            { identifier: "false", content: "False" },
          ],
          maxChoices: 1,
        },
        response: {
          correctValues: [((await getTFIsTrue(question)) ?? false).toString()],
        },
      };

      fields.push(field);
      break;
    }
    case QuestionType.Extended: {
      const field: QuestionFieldInput = {
        identifier: "RESPONSE",
        extendedInteraction: {
          wordLimit: question.responses[0]?.wordLimit ?? null,
        },
        response: {
          correctValues: [],
        },
      };

      fields.push(field);
      break;
    }

    case QuestionType.Blanks: {
      const { responses, respConditions } = question;
      const matchBlanks = (
        await Promise.all(
          responses.map(async (r) => {
            const responseId = getResponseId(r.id);

            if (!responseId) return [];

            let correctPhrases;
            if (r.choiceResponse?.contents != null) {
              correctPhrases = r.choiceResponse?.contents?.map(
                (c) => c.text?.trim() ?? ""
              );
            } else if (r.correctResponses != null) {
              correctPhrases = r.correctResponses;
            } else {
              correctPhrases = getCorrectResponses(responseId, respConditions);
            }

            return [
              {
                id: responseId,
                correctPhrases,
                phrases: await Promise.all(
                  (r.choiceResponse?.choices ?? []).map(
                    async (c) => (await getContentsText(c.contents)) ?? ""
                  )
                ),
              },
            ];
          })
        )
      ).flat();

      matchBlanks.forEach((blank) => {
        const textBlank =
          blank.phrases.length === 1 ||
          blank.phrases.length < blank.correctPhrases.length;

        fields.push({
          identifier: blank.id,
          ...(textBlank && { textEntryInteraction: {} }),
          ...(!textBlank && {
            choiceInteraction: {
              choices: blank.phrases.map((phrase) => ({
                identifier: getResponseId(phrase.trim()) ?? "",
                content: phrase.trim(),
              })),
              maxChoices: 1,
            },
          }),
          response: {
            correctValues: blank.correctPhrases,
            matchSimilarity: 1,
            caseSensitive: false,
          },
        });
      });
      break;
    }

    case QuestionType.Short: {
      const { respConditions, responses } = question;

      let matchPhrases: string[] = [];
      if (responses[0]?.correctResponses) {
        matchPhrases = responses[0].correctResponses;
      } else if (respConditions?.[0]?.conditions) {
        matchPhrases = respConditions?.[0]?.conditions?.map(
          (c) => c.condition?.value ?? ""
        );
      }

      const field: QuestionFieldInput = {
        identifier: "RESPONSE",
        textEntryInteraction: {},
        response: {
          correctValues: matchPhrases,
          matchSimilarity: 1,
          caseSensitive: false,
        },
      };
      fields.push(field);
      break;
    }
    case QuestionType.Hotspot: {
      const { responses, prompts } = question;
      await Promise.all(
        responses.map(async (response, index) => {
          if (!promptImage) return;
          let label = `${index + 1}`;
          const prompt = prompts?.[index];
          if (prompt) {
            label = await getContentsText(prompt.contents);
          }
          let correctAreas: PositionAreaInput[] = [];
          if (response.type === "point" && response.correctResponses) {
            correctAreas = response.correctResponses.map((r) => ({
              identifier: uuid(),
              shape: Shape.Point,
              coords: r.split(" ").map(Number),
            }));
          } else {
            correctAreas =
              response.correctAreas?.flatMap((area) => {
                const { width, height } = promptImage;
                const shape =
                  area.shape in Shape ? (area.shape as Shape) : Shape.Polygon;
                const coords = area.coords.every(
                  (point) => point >= 0 && point <= 1
                )
                  ? area.coords.map((point, index) => {
                      return index % 2 === 0 ? point * width : point * height;
                    })
                  : area.coords;
                return area.shape in Shape || area.shape === "bounded"
                  ? [
                      {
                        identifier: area.identifier,
                        shape,
                        coords,
                      },
                    ]
                  : [];
              }) ?? [];
          }
          const field: QuestionFieldInput = {
            identifier: response.id,
            response: {
              correctValues: [],
              correctAreas,
            },
            positionObjectInteraction: {
              label,
              maxChoices: 1,
            },
          };
          fields.push(field);
        })
      );
      break;
    }
  }
  return fields;
}

/**
 * Get correct answer from a TF question
 **/
async function getTFIsTrue(question: QTIQuestion) {
  const { responses, respConditions } = question;
  const response = responses[0];
  if (response?.correctResponses) {
    return response.correctResponses.some((resp) => /true/gi.test(resp));
  }
  if (!response?.choiceResponse) return;
  const correctAnswerCondition = respConditions.find(
    (r) =>
      r.vars.findIndex(
        (v) =>
          v.value && (v.value === `${v.name}.MAX` || parseFloat(v.value) > 0)
      ) !== -1
  );
  if (!correctAnswerCondition?.conditions) return;
  const correctAnswer = correctAnswerCondition.conditions.find((c) => {
    return c.condition?.value;
  });
  if (!correctAnswer) return;
  const correctChoice = response.choiceResponse.choices.find(
    ({ id }) => correctAnswer.condition && id === correctAnswer.condition.value
  );
  if (!correctChoice) return;
  const correctChoiceText = await getContentsText(correctChoice.contents);
  return (
    (/(true|false)$/gi.test(correctChoiceText) &&
      /true$/gi.test(correctChoiceText)) ||
    correctChoiceText.includes("t")
  );
}

/**
 * Get choices text and correctness of MCQ choices
 **/
async function getMCQChoices(
  rawChoices: QTIResponseContent[],
  respConditions: QTIResponseCondition[],
  correctResponses?: string[]
) {
  return (
    await Promise.all(
      rawChoices.map(async (c) => {
        const id = getResponseId(c.id);
        if (!id) return [] as Choice[];
        return [
          {
            id,
            text: await getContentsText(c.contents),
            isCorrect: getMCQIsCorrect(id, respConditions, correctResponses),
          } satisfies Choice,
        ];
      })
    )
  ).flat();
}

/**
 * Go through nested respConditions to check if there is
 * a condition where a response is considered correct (positive scores being set)
 **/
function getCorrectResponses(
  responseId: string,
  respConditions: QTIResponseCondition[]
) {
  return respConditions.flatMap((qtiCondition) => {
    const { vars, conditions } = qtiCondition;
    if (!conditions) return [];
    if (vars && getPositiveSetScoreVars(vars)) {
      return getCorrectResponseFromCondition(responseId, conditions);
    }
    return [];
  });
}

function getCorrectResponseFromCondition(
  responseId: string,
  conditions: ConditionVar[]
): string[] {
  return conditions.flatMap((c) => {
    const { condition, conditions } = c;
    if (conditions)
      return getCorrectResponseFromCondition(responseId, conditions);
    if (
      condition?.id &&
      getResponseId(condition?.id) === responseId &&
      condition.comparator === "eq" &&
      condition.value
    ) {
      return [condition.value];
    }
    return [];
  });
}

/**
 * Find if exists a qti response condition vars that sets
 * score to a positive score (> 0.0 or Max)
 **/
function getPositiveSetScoreVars(vars: QTIResponseConditionVar[]) {
  return vars.find(({ value, action }) => {
    const maybeNumberValue = value && parseFloat(value);
    return (
      action &&
      /set/gi.test(action) &&
      ((maybeNumberValue &&
        !isNaN(maybeNumberValue) &&
        maybeNumberValue > 0.0) ||
        (value && /max/gi.test(value)))
    );
  });
}

/**
 * Get the correctness of a specific choice from an MCQ question
 **/
function getMCQIsCorrect(
  choiceId: string,
  respConditions: QTIResponseCondition[],
  correctResponses?: string[]
) {
  if (correctResponses && correctResponses.includes(choiceId)) return true;
  if (respConditions.length === 1) {
    return (
      respConditions[0]?.conditions?.findIndex(
        ({ condition }) =>
          condition?.value === choiceId ||
          condition?.id === choiceId ||
          (condition?.value && getResponseId(condition?.value) == choiceId) ||
          (condition?.id && getResponseId(condition?.id) == choiceId)
      ) !== -1
    );
  } else {
    const positiveScores = respConditions.filter(({ vars }) => {
      return getPositiveSetScoreVars(vars);
    });
    if (!positiveScores?.length) return false;
    return positiveScores.some(
      ({ conditions }) =>
        conditions?.findIndex(
          ({ condition }) =>
            condition?.value === choiceId ||
            condition?.id === choiceId ||
            (condition?.value && getResponseId(condition?.value) == choiceId) ||
            (condition?.id && getResponseId(condition?.id) == choiceId)
        ) !== -1
    );
  }
}

/** get concatenated text of all QTI's materials' contents */
async function getContentsText(contents: QTIMaterial[]): Promise<string> {
  const editor = await getConcatenatedEditor(contents);
  try {
    return editor?.getText().trim() ?? "";
  } finally {
    editor?.destroy();
  }
}

/** matches an opening element tag like `<p`, `<img`, `<table` */
const ELEMENT_MARKUP = /<[a-z][\s\S]*?>/i;

/** concatenate all QTI's materials' contents to editor content*/
async function getConcatenatedEditor(
  contents: QTIMaterial[],
  file?: JSZipObject | null,
  zip?: JSZip,
  resources?: QTIResource[]
) {
  let editor: Editor | undefined;
  for (const content of contents) {
    const { text, textType } = content;
    if (!text) continue;
    let patchedText = text;
    if (textType && /html/i.test(textType)) {
      if (ELEMENT_MARKUP.test(text)) {
        // Material with real markup (images, tables, formatting) round-trips
        // through patchHTML; the editor decodes the HTML it re-serialises.
        patchedText =
          (await patchHTML(text, file, zip, resources)) ?? patchedText;
      } else {
        // Plain text never reaches the editor's HTML parser, so decode its
        // entities here. Sending it through patchHTML would re-encode them
        // (e.g. `>` -> `&gt;`) and the text insert would keep them raw.
        patchedText = decodeHtmlEntities(text);
      }
    }
    if (!editor) {
      editor = createQuestionPreviewEditor({
        id: "qtiParserEditor",
        promptDoc: null,
      });
    }
    editor.commands.insertContent(`\n${patchedText}`, {
      parseOptions: { preserveWhitespace: false },
    });
  }
  return editor;
}

/** get file from a zip file as a base64 string */
function getBase64File(
  file?: JSZipObject | null,
  zip?: JSZip,
  resources?: QTIResource[]
) {
  return async (src: string): Promise<Base64File | null> => {
    if (!file || !zip) return null;
    const lastSlashIndex = file.name.lastIndexOf("/");
    const path = file.name.substring(0, lastSlashIndex + 1);
    const getUrl = window.location;
    const baseUrl =
      getUrl.origin + getUrl.pathname.split("/").slice(0, -1).join("/");
    const imgSrc = src.replace(baseUrl, "");
    const srcRegex =
      /(((?:\/)?\$IMS-CC-FILEBASE\$\/|\/@X@EmbeddedFile.requestUrlStub@X@bbcswebdav\/)?([^"]+)(\?.*)?)|(.*)/;
    const match = srcRegex.exec(imgSrc);
    let base64String;
    let extension;
    let filename: string | undefined;
    if (match?.[3]) {
      filename = decodeURI(match[3]);
      if (filename.charAt(0) === "/") {
        filename = filename.slice(1);
      }
      const resource = resources?.find(
        (r) => filename && new RegExp(filename).test(r.filename)
      );
      if (resource) {
        base64String = await zip?.file(resource.path)?.async("base64");
        extension = await zip
          ?.file(resource.path)
          ?.async("uint8array")
          .then((a) => filetype(a)?.[0]?.extension);
      } else {
        base64String = await zip
          ?.file(new RegExp(filename))[0]
          ?.async("base64");
        extension = await zip
          ?.file(new RegExp(filename))[0]
          ?.async("uint8array")
          .then((a) => filetype(a)?.[0]?.extension);
      }
    } else if (match?.length === 1 && match[0]) {
      filename = decodeURI(match[0]);
      try {
        base64String = await zip?.file(path + filename)?.async("base64");
      } catch (_e) {
        base64String = await zip
          ?.file(new RegExp(filename))[0]
          ?.async("base64");
      }
      extension = await zip
        ?.file(path + match[0])
        ?.async("uint8array")
        .then((a) => filetype(a)?.[0]?.extension);
    }
    if (base64String && extension && filename) {
      return {
        base64String,
        extension,
        filename,
      };
    }
    return null;
  };
}

async function patchHTML(
  str: string,
  file?: JSZipObject | null,
  zip?: JSZip,
  resources?: QTIResource[]
) {
  return await basePatchHTML(str, getBase64File(file, zip, resources));
}

/**
 * Sometimes blanks will appear as `<span>[BLANK-1]</span>` instead of
 * `<interaction... identifier="BLANK-1">...</interaction>`.
 *
 * We convert these to specially tagged spans which our Blanks extension can
 * pick up and render as a Blank input.
 */
function patchNonInteractionBlanks(
  question: QTIQuestion,
  questionType: QuestionType | null
): QTIMaterial[] {
  const { contents, responses, convertedBlanksIds } = question;

  return contents.map((c) => {
    if (
      /html/i.test(c.textType ?? "") &&
      questionType === QuestionType.Blanks
    ) {
      let updatedText = c.text ?? "";
      responses.forEach((response) => {
        const responseId = getResponseId(response.id);
        if (responseId) {
          if (convertedBlanksIds.includes(responseId)) {
            // We've already converted this, don't convert again
            // fixes CADM-742
            return;
          }
          updatedText = updatedText.replace(
            `[${responseId}]`,
            `<span data-cadmus-blank="0" id="${responseId}">_____</span>`
          );
        }
      });
      return {
        ...c,
        text: updatedText,
      };
    }
    return c;
  });
}
